import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle, Mail, Building, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<any>(null);

  // Debug logging
  useEffect(() => {
    console.log('[InviteAccept] Component mounted', {
      token,
      pathname: window.location.pathname,
      search: window.location.search,
      href: window.location.href,
    });

    // Clear any stale React Query cache for invitations
    // This prevents old cached queries from being retried
    queryClient.removeQueries({ queryKey: ['invitation'] });
    console.log('[InviteAccept] Cleared React Query cache for invitations');
  }, [token, queryClient]);

  // Check if user is authenticated
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
      }
    });
  }, []);

  // Fetch invitation details via Edge Function (bypasses RLS for anonymous users)
  // CRITICAL: NO direct table queries - only Edge Function calls
  const { data: invitationData, isLoading: isLoadingInvitation, error: invitationError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: async () => {
      if (!token) {
        console.error('[InviteAccept] No invitation ID provided');
        return null;
      }

      console.log('[InviteAccept] Validating invitation via Edge Function:', token);
      console.log('[InviteAccept] Network call should be: /functions/v1/workspace-validate-invite');

      // Use Edge Function to validate invitation (bypasses RLS)
      // This is the ONLY way to validate invitations for anonymous users
      const { data, error } = await supabase.functions.invoke('workspace-validate-invite', {
        body: { invite_id: token },
      });

      console.log('[InviteAccept] Edge Function response:', { data, error, hasData: !!data });

      if (error) {
        console.error('[InviteAccept] Error validating invitation:', error);
        const notFoundError = new Error('This invitation link is invalid or has expired.');
        (notFoundError as any).code = 'INVITATION_NOT_FOUND';
        throw notFoundError;
      }

      if (!data?.valid) {
        console.warn('[InviteAccept] Invitation validation failed:', data?.reason);
        let errorMessage = 'This invitation link is invalid or has expired.';
        
        if (data?.reason === 'EXPIRED') {
          errorMessage = 'This invitation has expired. Please contact the person who invited you for a new invitation.';
        } else if (data?.reason === 'USED') {
          errorMessage = 'This invitation has already been used.';
        }
        
        const validationError = new Error(errorMessage);
        (validationError as any).code = 'INVITATION_NOT_FOUND';
        (validationError as any).reason = data?.reason;
        throw validationError;
      }

      console.log('[InviteAccept] Invitation validated:', data.invite?.id, data.invite?.email);

      return {
        ...data.invite,
        workspace: data.invite.workspace,
        inviter: data.invite.inviter || null,
        // Map the response to match expected structure
        workspace_id: data.invite.workspace?.id,
        status: 'pending', // Validated invitations are pending
      };
    },
    enabled: !!token,
    retry: false, // Disable retries to prevent stale query attempts
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: true, // Only refetch on mount (not on every render)
  });

  // Map the validated invitation data to the expected format
  const invitation = invitationData;

  // Accept invitation
  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!token || !user) {
        throw new Error('Missing invitation ID or user');
      }

      // Note: The 'token' parameter here is actually the invitation ID
      // The Edge Function needs to be updated to accept invitation_id and query by id
      const { data, error } = await supabase.functions.invoke('workspace-accept-invitation', {
        body: {
          invitation_id: token, // Pass the invitation ID
          user_id: user.id,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Welcome to BillPortal!',
        description: 'You have successfully joined the workspace.',
      });
      
      // Navigate to the workspace dashboard
      if (data?.workspace_id) {
        navigate(`/w/${data.workspace_id}/dashboard`);
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to accept invitation',
        variant: 'destructive',
      });
    },
  });

  // Show error if query failed
  if (invitationError) {
    console.error('[InviteAccept] Query error:', invitationError);
    
    const errorCode = (invitationError as any)?.code;
    const isNotFound = errorCode === 'INVITATION_NOT_FOUND' || 
                      errorCode === 'PGRST116' ||
                      (invitationError as any)?.message?.includes('0 rows');
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              {isNotFound ? 'Invitation Not Found' : 'Error Loading Invitation'}
            </CardTitle>
            <CardDescription>
              {isNotFound 
                ? 'This invitation link is invalid or has expired. Please contact the person who invited you for a new invitation.'
                : (invitationError instanceof Error 
                    ? invitationError.message 
                    : 'Failed to load invitation. Please check the link and try again.')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isNotFound && (
              <div className="text-sm text-muted-foreground">
                <p>Invitation ID: {token || 'Not provided'}</p>
                <p>Path: {window.location.pathname}</p>
              </div>
            )}
            <Button asChild className="w-full">
              <Link to="/auth">Go to Login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoadingInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading invitation...</p>
              {token && (
                <p className="text-xs text-muted-foreground">Token: {token.substring(0, 8)}...</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              Invitation Not Found
            </CardTitle>
            <CardDescription>
              This invitation link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground">
              <p>Token: {token || 'Not provided'}</p>
              <p>If you believe this is an error, please contact the person who invited you.</p>
            </div>
            <Button asChild className="w-full">
              <Link to="/auth">Go to Login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Note: Expiration and status validation is handled by the Edge Function
  // If we reach here, the invitation is valid and pending
  const isAccepted = invitation.status === 'accepted';
  const isDeclined = invitation.status === 'declined';

  if (isAccepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Invitation Already Accepted
            </CardTitle>
            <CardDescription>
              You have already accepted this invitation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{invitation.workspace?.name}</span>
              </div>
            </div>
            <Button asChild className="w-full">
              <Link to={`/w/${invitation.workspace_id}/dashboard`}>
                Go to Workspace
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isDeclined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Invitation Declined
            </CardTitle>
            <CardDescription>
              This invitation has been declined.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/auth">Go to Login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Welcome to BillPortal!</CardTitle>
          <CardDescription className="text-base">
            You've been invited to join a workspace
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="space-y-4 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building className="h-5 w-5 text-muted-foreground" />
                <span className="font-semibold">Workspace</span>
              </div>
              <Badge variant="outline">{invitation.workspace?.name}</Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-muted-foreground" />
                <span className="font-semibold">Role</span>
              </div>
              <Badge>{invitation.role}</Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Invited by</span>
              <span className="text-sm font-medium">
                {invitation.inviter?.full_name || invitation.inviter?.email || 'Unknown'}
              </span>
            </div>

            {invitation.module_access && invitation.module_access.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium mb-2">Module Access:</p>
                <div className="flex flex-wrap gap-2">
                  {invitation.module_access.map((module: any) => (
                    <Badge key={module.module_id} variant="secondary">
                      {module.module_id}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {!user ? (
            <div className="space-y-3">
              <p className="text-sm text-center text-muted-foreground">
                Please sign in or create an account to accept this invitation
              </p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" asChild>
                  <Link to={`/auth?redirect=/invite/${token}`}>Sign In</Link>
                </Button>
                <Button className="flex-1" asChild>
                  <Link to={`/auth?redirect=/invite/${token}&mode=signup`}>
                    Create Account
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-center text-muted-foreground">
                Click below to accept this invitation and join the workspace
              </p>
              <Button
                className="w-full"
                size="lg"
                onClick={() => acceptMutation.mutate()}
                disabled={acceptMutation.isPending}
              >
                {acceptMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Accept Invitation
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

